import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IconHeadphones, IconMusic, IconWaveSine } from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { recordToolUsage } from '../../../../../services/toolsService';
import VideoDropZone from '../../components/video/VideoDropZone';
import useVideoPaste from '../../components/video/useVideoPaste';
import { VideoFileRow } from '../../components/video/VideoFileRow';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import ToolCard from '../../components/common/ToolCard';
import ToolReadyHint from '../../components/common/ToolReadyHint';
import { videoTileClass, videoTrayClass } from '../../components/video/VideoOptionTiles';
import {
    MAX_CONVERT_VIDEO_BYTES,
    convertedAudioName,
    formatFileSize,
    isSupportedVideoFile,
    videoToAudio,
    preloadFFmpeg,
} from './convertVideoEngine';
import {
    deriveToolStatus,
    downloadBlob,
    getToolStatusText,
} from '../../toolPageUtils';

const FORMAT_OPTIONS = [
    { id: 'mp3', labelKey: 'convertVideo.formats.mp3', descKey: 'convertVideo.formats.mp3Desc', Icon: IconMusic },
    { id: 'aac', labelKey: 'convertVideo.formats.aac', descKey: 'convertVideo.formats.aacDesc', Icon: IconHeadphones },
    { id: 'wav', labelKey: 'convertVideo.formats.wav', descKey: 'convertVideo.formats.wavDesc', Icon: IconWaveSine },
];

export default function ConvertVideo() {
    const { t } = useTranslation();
    const fileInputRef = useRef(null);

    const [file, setFile] = useState(null);
    const [targetFormat, setTargetFormat] = useState('mp3');
    const [isDragging, setIsDragging] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [progress, setProgress] = useState(null);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const videoPreviewUrl = useMemo(
        () => (file ? URL.createObjectURL(file) : ''),
        [file],
    );

    const resultPreviewUrl = useMemo(
        () => (result ? URL.createObjectURL(result.blob) : ''),
        [result],
    );

    useEffect(() => () => {
        if (videoPreviewUrl) URL.revokeObjectURL(videoPreviewUrl);
    }, [videoPreviewUrl]);

    useEffect(() => () => {
        if (resultPreviewUrl) URL.revokeObjectURL(resultPreviewUrl);
    }, [resultPreviewUrl]);

    useEffect(() => {
        preloadFFmpeg();
    }, []);

    const addFile = useCallback((incoming) => {
        const list = Array.from(incoming || []);
        const videos = list.filter(isSupportedVideoFile);
        if (!videos.length) {
            const msg = t('convertVideo.errors.invalidType');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }

        const next = videos[0];
        if (next.size > MAX_CONVERT_VIDEO_BYTES) {
            const msg = t('convertVideo.errors.tooLarge');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }

        setFile(next);
        setResult(null);
        setError(null);
        setProgress(null);
        setTargetFormat('mp3');
    }, [t]);

    // Ctrl/Cmd+V anywhere on the page, the same as the image and PDF
    // tools. Ignored while a run is in flight: the clip would be dropped
    // on the floor by the reset that follows it.
    useVideoPaste(addFile, { enabled: !processing });

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        addFile(e.dataTransfer.files);
    };

    const handleReset = () => {
        setFile(null);
        setResult(null);
        setError(null);
        setProgress(null);
        setTargetFormat('mp3');
    };

    const invalidateResult = () => {
        setResult(null);
        setError(null);
        setProgress(null);
    };

    const resolveErrorMessage = (err) => {
        const code = err?.message;
        if (code === 'OUT_OF_MEMORY') return t('convertVideo.errors.outOfMemory');
        if (code === 'TIMEOUT') return t('convertVideo.errors.timeout');
        if (code === 'FFMPEG_NOT_LOADED') return t('convertVideo.errors.engine');
        if (code === 'ENCODE_FAILED' || code === 'EMPTY_OUTPUT' || code === 'EXTRACT_FAILED') {
            return t('convertVideo.errors.failed');
        }
        return t('convertVideo.errors.failed');
    };

    const handleConvert = async () => {
        if (!file || processing) return;

        recordToolUsage('video/convert', { category: 'video' });

        setProcessing(true);
        setResult(null);
        setError(null);
        setProgress(0);

        try {
            const blob = await videoToAudio(file, targetFormat, (value) => setProgress(value));
            const output = {
                name: convertedAudioName(file.name, targetFormat),
                originalSize: file.size,
                audioSize: blob.size,
                format: targetFormat,
                blob,
            };
            setResult(output);
            notifications.show({
                title: t('common.success'),
                message: t('convertVideo.successNotice', { format: targetFormat.toUpperCase() }),
                color: 'green',
            });
        } catch (err) {
            const msg = resolveErrorMessage(err);
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        } finally {
            setProcessing(false);
            setProgress(null);
        }
    };

    const status = deriveToolStatus({
        hasInput: !!file,
        canRun: !!file,
        processing,
        hasResult: !!result,
        hasError: !!error,
    });

    const howToSteps = useMemo(
        () => [1, 2, 3, 4].map((i) => t(`convertVideo.howTo.step${i}`)),
        [t],
    );

    const sizeNote = result ? (
        <>
            <span className="font-semibold text-[#3D3833] dark:text-zinc-200">
                {formatFileSize(result.originalSize)}
            </span>
            <span className="text-[#A8A099] dark:text-zinc-500">→</span>
            <span className="font-semibold text-[#1E9E5E]">
                {formatFileSize(result.audioSize)}
            </span>
            <span className="text-[#A8A099] dark:text-zinc-500">
                ({result.format.toUpperCase()})
            </span>
        </>
    ) : null;

    const statusText = getToolStatusText(status, t, {
        ready: t('toolPage.statusText.ready'),
        success: t('convertVideo.done'),
    });

    const resultDownloads = result ? [{
        id: result.name,
        name: result.name,
        originalSize: formatFileSize(result.originalSize),
        compressedSize: formatFileSize(result.audioSize),
        onDownload: () => downloadBlob(result.blob, result.name),
    }] : undefined;

    return (
        <ToolPageLayout
            slug="video/convert"
            subtitleKey="convertVideo.subtitle"
            status={status}
            statusText={statusText}
            controlsTitle={t('toolPage.files', { count: toBnDigits(file ? 1 : 0) })}
            showAdd={false}
            howToSteps={howToSteps}
            onReset={file || result || error ? handleReset : undefined}
            primaryAction={{
                label: t('convertVideo.convert', { format: targetFormat.toUpperCase() }),
                onClick: handleConvert,
                disabled: !file || processing || !!result,
                loading: processing,
                icon: <IconMusic size={18} stroke={1.9} />,
            }}
            controls={(
                <>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="video/*,.mp4,.mov,.avi,.mkv,.webm,.m4v"
                        className="hidden"
                        onChange={(e) => { addFile(e.target.files); e.target.value = ''; }}
                    />

                    {!file ? (
                        <VideoDropZone
                            title={t('convertVideo.dropTitle')}
                            hint={t('convertVideo.dropHint')}
                            isDragging={isDragging}
                            onClick={() => fileInputRef.current?.click()}
                            onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
                            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                            onDragLeave={() => setIsDragging(false)}
                            onDrop={handleDrop}
                        />
                    ) : (
                        <VideoFileRow
                            name={file.name}
                            meta={formatFileSize(file.size)}
                            onRemove={handleReset}
                            removeLabel={t('convertVideo.removeFile')}
                        />
                    )}
                </>
            )}
            settings={file ? (
                <ToolCard title={t('convertVideo.formatTitle')}>
                    <div className={videoTrayClass('grid-cols-3')}>
                        {FORMAT_OPTIONS.map(({ id, labelKey, descKey, Icon }) => (
                            <button
                                key={id}
                                type="button"
                                onClick={() => {
                                    setTargetFormat(id);
                                    invalidateResult();
                                }}
                                title={`${t(labelKey)} — ${t(descKey)}`}
                                aria-pressed={targetFormat === id}
                                className={videoTileClass(targetFormat === id)}
                            >
                                <Icon size={20} stroke={1.7} />
                                {t(labelKey)}
                            </button>
                        ))}
                    </div>
                    {/* The picked format's trade-off — the tiles no longer spell it out. */}
                    <p className="mt-2 text-[11.5px] leading-snug text-[#A8A099] dark:text-zinc-500">
                        {t(FORMAT_OPTIONS.find((opt) => opt.id === targetFormat)?.descKey || 'convertVideo.formats.mp3Desc')}
                    </p>
                </ToolCard>
            ) : null}
            preview={(
                <ToolPreviewPanel
                    title={t('convertVideo.previewTitle')}
                    status={status}
                    statusLabel={t(`toolPage.status.${status}`)}
                    idleTitle={t('convertVideo.previewEmpty')}
                    idleHint={t('convertVideo.previewEmptyHint')}
                    processingLabel={t('convertVideo.converting')}
                    progress={progress ?? undefined}
                    error={error}
                    successLabel={t('convertVideo.done')}
                    downloadLabel={t('convertVideo.download')}
                    downloads={resultDownloads}
                    onDownload={result ? () => downloadBlob(result.blob, result.name) : undefined}
                    onAgain={result ? handleReset : undefined}
                    againLabel={t('convertVideo.startOver')}
                    sizeNote={sizeNote}
                    readyHint={status === 'ready' ? <ToolReadyHint actionLabel={t('convertVideo.convert', { format: targetFormat.toUpperCase() })} /> : null}
                    onIdleClick={() => fileInputRef.current?.click()}
                    onIdleDrop={handleDrop}
                    onIdleDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onIdleDragLeave={() => setIsDragging(false)}
                    idleDragging={isDragging}
                    showIdle={!file && !error}
                >
                    {file && !processing && !error ? (
                        <div className="flex h-full min-h-[280px] flex-col">
                            {result ? (
                                <div className="flex flex-1 flex-col items-center justify-center gap-4 rounded-xl border border-[#ECE8E4] bg-violet-50 px-6 py-8 dark:border-zinc-700 dark:bg-violet-900/20">
                                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-violet-600 shadow-[0_1px_2px_rgba(28,24,21,0.04)] dark:bg-zinc-900 dark:text-violet-400">
                                        <IconMusic size={28} stroke={1.6} />
                                    </div>
                                    <audio
                                        src={resultPreviewUrl}
                                        controls
                                        className="w-full max-w-md"
                                    />
                                </div>
                            ) : (
                                <div className="flex flex-1 items-center justify-center overflow-hidden rounded-xl border border-[#ECE8E4] bg-black dark:border-zinc-700">
                                    <video
                                        src={videoPreviewUrl}
                                        controls
                                        className="max-h-[360px] w-full max-w-full"
                                    />
                                </div>
                            )}
                            <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-[11.5px] text-[#A8A099] dark:text-zinc-500">
                                <span className="truncate font-medium text-[#3D3833] dark:text-zinc-300">
                                    {result ? result.name : file.name}
                                </span>
                                <span>
                                    {result
                                        ? `${formatFileSize(result.audioSize)} · ${result.format.toUpperCase()}`
                                        : `${t('convertVideo.outputBadge', { format: targetFormat.toUpperCase() })} · ${formatFileSize(file.size)}`}
                                </span>
                            </div>
                        </div>
                    ) : null}
                </ToolPreviewPanel>
            )}
        />
    );
}
